/**
 * CampaignMessageBubble - Balão estilo WhatsApp para um disparo de campanha
 *
 * Renderiza o conteúdo REAL enviado ao contato (texto, imagem, vídeo, áudio,
 * documento, localização, template oficial com botões), reaproveitando os
 * componentes de balão já usados no módulo de chat (sem duplicar renderização).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (aba Campanhas do modal de cliente/contato)
 */
import React from 'react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

import { ImageMessageBubble } from '@/components/tenant/chat/ImageMessageBubble';
import { VideoMessageBubble } from '@/components/tenant/chat/VideoMessageBubble';
import { AudioMessageBubble } from '@/components/tenant/chat/AudioMessageBubble';
import { DocumentMessageBubble } from '@/components/tenant/chat/DocumentMessageBubble';
import { LocationMessageBubble } from '@/components/tenant/chat/LocationMessageBubble';
import { TemplateMessageBubble } from '@/components/tenant/chat/TemplateMessageBubble';
import { ButtonListMessageBubble } from '@/components/tenant/chat/ButtonListMessageBubble';
import { MessageStatusIndicator } from '@/components/tenant/chat/MessageStatusIndicator';
import { ImageLightbox } from '@/components/tenant/chat/ImageLightbox';

import type { ContactCampaignHistoryItem } from '@/hooks/useContactCampaignHistory';

interface CampaignMessageBubbleProps {
  /** Disparo retornado pela RPC get_contact_campaign_history */
  item: ContactCampaignHistoryItem;
}

/** Normaliza a lista de botões vinda do metadata (template ou botões simples) */
function extractButtons(metadata: Record<string, any> | null | undefined) {
  if (!metadata) return undefined;
  const raw = metadata.template_buttons ?? metadata.buttons ?? metadata.buttons_config;
  return Array.isArray(raw) ? raw : undefined;
}

/**
 * Balão de saída (outbound) com o conteúdo do disparo.
 */
export function CampaignMessageBubble({ item }: CampaignMessageBubbleProps) {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  const metadata = (item.message_metadata ?? null) as Record<string, any> | null;
  const type = (item.message_type ?? 'text').toLowerCase();
  const mediaUrl = item.media_url ?? metadata?.media_url ?? null;
  const caption = metadata?.media_caption ?? item.message_content ?? null;

  // Conteúdo interno do balão conforme o tipo da mensagem enviada
  const renderContent = () => {
    switch (type) {
      case 'image':
        return mediaUrl ? (
          <>
            <ImageMessageBubble
              url={mediaUrl}
              caption={item.message_content}
              direction="outbound"
              onOpenLightbox={() => setLightboxOpen(true)}
            />
            <ImageLightbox
              open={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              imageUrl={mediaUrl}
              caption={caption}
            />
          </>
        ) : (
          <p className="whitespace-pre-wrap break-words">{item.message_content}</p>
        );

      case 'video':
        return mediaUrl ? (
          <VideoMessageBubble url={mediaUrl} caption={item.message_content} direction="outbound" />
        ) : (
          <p className="whitespace-pre-wrap break-words">{item.message_content}</p>
        );

      case 'audio':
      case 'ptt':
        return mediaUrl ? (
          <AudioMessageBubble
            url={mediaUrl}
            duration={metadata?.duration}
            isPTT={type === 'ptt'}
            direction="outbound"
          />
        ) : (
          <p className="whitespace-pre-wrap break-words">{item.message_content}</p>
        );

      case 'document':
        return mediaUrl ? (
          <DocumentMessageBubble
            url={mediaUrl}
            filename={metadata?.media_filename ?? metadata?.filename ?? item.message_content}
            mimeType={metadata?.media_mime_type ?? metadata?.mime_type}
            direction="outbound"
          />
        ) : (
          <p className="whitespace-pre-wrap break-words">{item.message_content}</p>
        );

      case 'location':
        return (
          <LocationMessageBubble
            latitude={Number(metadata?.latitude ?? 0)}
            longitude={Number(metadata?.longitude ?? 0)}
            name={metadata?.name}
            address={metadata?.address}
            direction="outbound"
          />
        );

      case 'template':
        return (
          <TemplateMessageBubble
            templateName={metadata?.template_name ?? 'template'}
            templateBody={metadata?.template_body ?? item.message_content ?? ''}
            direction="outbound"
            buttons={extractButtons(metadata)}
            headerFormat={metadata?.header_format}
            headerMediaUrl={metadata?.header_media_url}
          />
        );

      case 'button_list':
      case 'buttons': {
        const buttons = extractButtons(metadata) ?? [];
        return (
          <ButtonListMessageBubble
            content={item.message_content ?? ''}
            options={buttons.map((b: any, i: number) => ({
              id: String(b.id ?? b.buttonId ?? i),
              label: String(b.text ?? b.label ?? b.title ?? ''),
            }))}
            direction="outbound"
          />
        );
      }

      default:
        return <p className="whitespace-pre-wrap break-words">{item.message_content || '—'}</p>;
    }
  };

  return (
    <div className="flex justify-end">
      <div
        className={cn(
          'relative max-w-[85%] md:max-w-[70%] rounded-lg rounded-tr-none',
          'bg-[hsl(var(--chat-bubble-outbound))] text-foreground',
          'px-2.5 md:px-4 py-1.5 shadow-sm text-[13px] leading-snug'
        )}
      >
        {renderContent()}

        {/* Rodapé: horário + status de entrega (padrão WhatsApp) */}
        <div className="mt-0.5 flex items-center justify-end gap-1 text-[10px] text-muted-foreground">
          {item.is_fallback && <span className="italic mr-1">conteúdo previsto</span>}
          <span>{item.sent_at ? format(new Date(item.sent_at), 'HH:mm') : ''}</span>
          <MessageStatusIndicator
            status={(item.status ?? 'sent') as any}
            direction="outbound"
            className="text-muted-foreground"
          />
        </div>
      </div>
    </div>
  );
}
